/**
 * React Query hooks for tutors, students, volunteers, and staff users.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import { shouldRetryUnitGet } from '@/hooks/useUnitGetNotFound';
import { getAdmins, getAdmin } from '@/services/admins.service';
import {
  getManagers,
  getManager,
  getCoordinators,
  getCoordinator,
  getSuperadmins,
  getSuperadmin,
  createProspect,
  getCriminalRecord,
  updateCriminalRecordDecision,
} from '@/services/users.service';
import { getVolunteers, getVolunteer } from '@/services/volunteers.service';
import type {
  ArchiveEligibility,
  ArchiveRemoteUserDto,
  ArchiveRemoteUserResult,
  CriminalRecordDto,
  CriminalRecordDecisionAction,
  InviteProspectDto,
  ReactivationEligibility,
  ReactivateRemoteUserResult,
} from '@/types/user.types';
import {
  getTutors,
  getTutor,
  createProfile as createInPersonTutorProfile,
  updateProfile as updateInPersonTutorProfile,
  getFullState as getInPersonTutorFullState,
  getEligibleEstablishments,
  list as listInPersonTutors,
  markInformationMeetingParticipation as markTutorInPersonInformationMeetingParticipation,
  listAssignments as listTutorInPersonAssignments,
  retainEstablishments,
  refuseAllEstablishments,
  reassignEstablishment,
  validateAssignment,
  refuseAssignment,
  coordinatorDirectAssign,
} from '@/services/inperson/tutors.service';
import {
  createProfile as createTutorRemoteProfile,
  updateProfile as updateTutorRemoteProfile,
  listScoped as listTutorsRemoteScoped,
  getFullState as getTutorRemoteFullState,
  markInformationSessionParticipation,
  getArchiveEligibility as getTutorRemoteArchiveEligibility,
  archiveUser as archiveTutorRemote,
  getReactivationEligibility as getTutorRemoteReactivationEligibility,
  reactivateUser as reactivateTutorRemote,
} from '@/services/remote/tutors.service';
import { getStudents, getStudent } from '@/services/inperson/students.service';
import {
  createProfile as createStudentRemoteProfile,
  updateProfile as updateStudentRemoteProfile,
  listScoped as listStudentsRemoteScoped,
  getFullState as getStudentRemoteFullState,
  markWelcomeMeetingParticipation,
  getArchiveEligibility as getStudentRemoteArchiveEligibility,
  archiveUser as archiveStudentRemote,
  getReactivationEligibility as getStudentRemoteReactivationEligibility,
  reactivateUser as reactivateStudentRemote,
} from '@/services/remote/students.service';
import {
  createProfile as createParentRemoteProfile,
  updateProfile as updateParentRemoteProfile,
  list as listParentsRemote,
  listScoped as listParentsRemoteScoped,
  getFullState as getParentRemoteFullState,
  getChildren as getParentRemoteChildren,
  getArchiveEligibility as getParentRemoteArchiveEligibility,
  archiveUser as archiveParentRemote,
  getReactivationEligibility as getParentRemoteReactivationEligibility,
  reactivateUser as reactivateParentRemote,
} from '@/services/parents.service';
import type {
  TutorRecord,
  OnboardingFilters as TutorFilters,
  FullState as TutorFullState,
  ProfileDto as TutorRemoteProfileDto,
} from '@/types/remote/tutor.types';
import type {
  ProfileDto as InPersonTutorProfileDto,
  ListFilters as InPersonTutorListFilters,
} from '@/types/inperson/tutor.types';
import type {
  StudentRecord,
  OnboardingFilters as StudentFilters,
  FullState as StudentFullState,
  ProfileDto as StudentRemoteProfileDto,
} from '@/types/remote/student.types';
import type {
  ParentRecord,
  OnboardingFilters as ParentFilters,
  FullState as ParentFullState,
  ProfileDto as ParentRemoteProfileDto,
} from '@/types/parent.types';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { sessionKeys } from '@/hooks/useSessionsRemote';

/** Shared list query params for user-role hooks ({@link useTutors}, {@link useStudents}, …). */
interface UseUsersParams {
  search?: string;
  page?: number;
  pageSize?: number;
  excludeIds?: string[];
}

/** `GET /users/tutors` — paginated in-person tutors. */
export const useTutors = (params?: UseUsersParams) => {
  return useQuery({
    queryKey: ['users', 'tutors', params],
    queryFn: () => getTutors(params),
  });
};

/** `GET /users/tutors/:id` */
export const useTutor = (id: string) => {
  return useQuery({
    queryKey: ['users', 'tutors', id],
    queryFn: () => getTutor(id),
    enabled: !!id,
  });
};

/** `GET /users/students` — paginated in-person students. */
export const useStudents = (params?: UseUsersParams) => {
  return useQuery({
    queryKey: ['users', 'students', params],
    queryFn: () => getStudents(params),
  });
};

/** `GET /users/students/:id` */
export const useStudent = (id: string) => {
  return useQuery({
    queryKey: ['users', 'students', id],
    queryFn: () => getStudent(id),
    enabled: !!id,
  });
};

/** `GET /users/volunteers` — paginated volunteers. */
export const useVolunteers = (params?: UseUsersParams) => {
  return useQuery({
    queryKey: ['users', 'volunteers', params],
    queryFn: () => getVolunteers(params),
  });
};

/** `GET /users/volunteers/:id` */
export const useVolunteer = (id: string) => {
  return useQuery({
    queryKey: ['users', 'volunteers', id],
    queryFn: () => getVolunteer(id),
    enabled: !!id,
  });
};

/** `GET /users/admins` — paginated admins. */
export const useAdmins = (params?: UseUsersParams) => {
  return useQuery({
    queryKey: ['users', 'admins', params],
    queryFn: () => getAdmins(params),
  });
};

/** `GET /users/admins/:id` */
export const useAdmin = (id: string) => {
  return useQuery({
    queryKey: ['users', 'admins', id],
    queryFn: () => getAdmin(id),
    enabled: !!id,
  });
};

/** `GET /users/managers` — paginated managers. */
export const useManagers = (params?: UseUsersParams) => {
  return useQuery({
    queryKey: ['users', 'managers', params],
    queryFn: () => getManagers(params),
  });
};

/** `GET /users/managers/:id` */
export const useManager = (id: string) => {
  return useQuery({
    queryKey: ['users', 'managers', id],
    queryFn: () => getManager(id),
    enabled: !!id,
  });
};

/** `GET /users/coordinators` — paginated coordinators. */
export const useCoordinators = (params?: UseUsersParams) => {
  return useQuery({
    queryKey: ['users', 'coordinators', params],
    queryFn: () => getCoordinators(params),
  });
};

/** `GET /users/coordinators/:id` */
export const useCoordinator = (id: string) => {
  return useQuery({
    queryKey: ['users', 'coordinators', id],
    queryFn: () => getCoordinator(id),
    enabled: !!id,
  });
};

/** `GET /users/superadmins` — paginated superadmins. */
export const useSuperadmins = (params?: UseUsersParams) => {
  return useQuery({
    queryKey: ['users', 'superadmins', params],
    queryFn: () => getSuperadmins(params),
  });
};

/** `GET /users/superadmins/:id` */
export const useSuperadmin = (id: string) => {
  return useQuery({
    queryKey: ['users', 'superadmins', id],
    queryFn: () => getSuperadmin(id),
    enabled: !!id,
  });
};

/** `POST /tutors-remote` — create tutor + onboarding; invalidates onboarding lists. */
export function useCreateTutorRemoteProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ dto, companyId }: { dto: TutorRemoteProfileDto; companyId: string }) =>
      createTutorRemoteProfile(dto, companyId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['onboarding'] });
    },
  });
}

/** `PATCH /tutors-remote/:userId` — invalidates onboarding lists. */
export function useUpdateTutorRemoteProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      dto,
      companyId,
    }: {
      userId: string;
      dto: TutorRemoteProfileDto;
      companyId: string;
    }) => updateTutorRemoteProfile(userId, dto, companyId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['onboarding'] });
    },
  });
}

/** `POST /tutors-presentiel` — create in-person tutor; invalidates onboarding + users-search. */
export function useCreateInPersonTutorProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ dto, companyId }: { dto: InPersonTutorProfileDto; companyId: string }) =>
      createInPersonTutorProfile(dto, companyId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['onboarding'] });
      void queryClient.invalidateQueries({ queryKey: ['users-search', 'tutor'] });
    },
  });
}

/** `PATCH /tutors-presentiel/:userId` — invalidates onboarding, users-search, tutors. */
export function useUpdateInPersonTutorProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      dto,
      companyId,
    }: {
      userId: string;
      dto: InPersonTutorProfileDto;
      companyId: string;
    }) => updateInPersonTutorProfile(userId, dto, companyId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['onboarding'] });
      void queryClient.invalidateQueries({ queryKey: ['users-search', 'tutor'] });
      void queryClient.invalidateQueries({ queryKey: ['tutors'] });
    },
  });
}

/** `GET /tutors-presentiel/:userId/full-state` */
export function useGetInPersonTutorFullState(
  userId: string,
  companyId: string,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['onboarding', 'tutor-presentiel', userId, companyId],
    queryFn: () => getInPersonTutorFullState(userId, companyId),
    ...options,
    retry: shouldRetryUnitGet,
  });
}

/** `GET /tutors-presentiel/eligible-establishments` */
export function useEligibleEstablishments(
  departments: string[],
  companyId: string,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['eligible-establishments', departments, companyId],
    queryFn: () => getEligibleEstablishments(departments, companyId),
    enabled: departments.length > 0 && options?.enabled !== false,
  });
}

/** `GET /tutors-presentiel` — paginated in-person tutor onboarding list. */
export function useListInPersonTutors(filters: InPersonTutorListFilters) {
  return useQuery({
    queryKey: ['tutors', filters],
    queryFn: () => listInPersonTutors(filters),
  });
}

/** `GET /tutors-presentiel/:userId/assignments` */
export function useInPersonTutorAssignments(userId: string, companyId: string) {
  return useQuery({
    queryKey: ['tutor-presentiel-affectations', userId, companyId],
    queryFn: () => listTutorInPersonAssignments(userId, companyId),
    enabled: !!userId && !!companyId,
  });
}

/** `POST /tutors-presentiel/:userId/information-meeting` — invalidates tutor full-state + tutors. */
export function useMarkInPersonTutorInformationMeetingParticipation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      companyId,
      params,
    }: {
      userId: string;
      companyId: string;
      params: { sessionId?: string; participated?: boolean; participatedAt?: string };
    }) => markTutorInPersonInformationMeetingParticipation(userId, companyId, params),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['onboarding', 'tutor-presentiel', variables.userId],
      });
      void queryClient.invalidateQueries({
        queryKey: ['tutors'],
      });
    },
  });
}

/** `POST /tutors-presentiel/:userId/retain-establishments` — invalidates assignments + tutors. */
export function useRetainEstablishments() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      companyId,
      establishmentIds,
    }: {
      userId: string;
      companyId: string;
      establishmentIds: string[];
    }) => retainEstablishments(userId, companyId, establishmentIds),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['tutor-presentiel-affectations', variables.userId],
      });
      void queryClient.invalidateQueries({
        queryKey: ['tutors'],
      });
    },
  });
}

/** `POST /tutors-presentiel/:userId/refuse-all-establishments` — invalidates assignments + tutors. */
export function useRefuseAllEstablishments() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      companyId,
      reason,
      establishmentIds,
    }: {
      userId: string;
      companyId: string;
      reason: string;
      establishmentIds?: string[];
    }) => refuseAllEstablishments(userId, companyId, reason, establishmentIds),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['tutor-presentiel-affectations', variables.userId],
      });
      void queryClient.invalidateQueries({
        queryKey: ['tutors'],
      });
    },
  });
}

/** `POST /tutors-presentiel/:userId/reassign-establishment` — invalidates assignments + tutors. */
export function useReassignEstablishment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      companyId,
      establishmentId,
    }: {
      userId: string;
      companyId: string;
      establishmentId: string;
    }) => reassignEstablishment(userId, companyId, establishmentId),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['tutor-presentiel-affectations', variables.userId],
      });
      void queryClient.invalidateQueries({
        queryKey: ['tutors'],
      });
    },
  });
}

/** `POST /tutors-presentiel/:userId/assignments/:affectationId/validate` — invalidates assignments + tutors. */
export function useValidateAssignment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      companyId,
      affectationId,
      comments,
    }: {
      userId: string;
      companyId: string;
      affectationId: string;
      comments?: string;
    }) => validateAssignment(userId, companyId, affectationId, comments),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['tutor-presentiel-affectations', variables.userId],
      });
      void queryClient.invalidateQueries({
        queryKey: ['tutors'],
      });
    },
  });
}

/** `POST /tutors-presentiel/:userId/assignments/:affectationId/refuse` — invalidates assignments + tutors. */
export function useRefuseAssignment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      companyId,
      affectationId,
      reason,
    }: {
      userId: string;
      companyId: string;
      affectationId: string;
      reason: string;
    }) => refuseAssignment(userId, companyId, affectationId, reason),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['tutor-presentiel-affectations', variables.userId],
      });
      void queryClient.invalidateQueries({
        queryKey: ['tutors'],
      });
    },
  });
}

/** `POST /tutors-presentiel/:userId/coordinator-direct-assign` — invalidates assignments + tutors. */
export function useCoordinatorDirectAssign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      companyId,
      establishmentId,
      comments,
    }: {
      userId: string;
      companyId: string;
      establishmentId: string;
      comments?: string;
    }) => coordinatorDirectAssign(userId, companyId, establishmentId, comments),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['tutor-presentiel-affectations', variables.userId],
      });
      void queryClient.invalidateQueries({
        queryKey: ['tutors'],
      });
    },
  });
}

/** `POST /parents-remote` — create parent + onboarding; invalidates onboarding lists. */
export function useCreateParentRemoteProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ dto, companyId }: { dto: ParentRemoteProfileDto; companyId: string }) =>
      createParentRemoteProfile(dto, companyId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['onboarding'] });
    },
  });
}

/** `PATCH /parents-remote/:userId` — invalidates onboarding lists. */
export function useUpdateParentRemoteProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      dto,
      companyId,
    }: {
      userId: string;
      dto: ParentRemoteProfileDto;
      companyId: string;
    }) => updateParentRemoteProfile(userId, dto, companyId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['onboarding'] });
    },
  });
}

/** `POST /students-remote` — create student + onboarding; invalidates onboarding lists. */
export function useCreateStudentRemoteProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ dto, companyId }: { dto: StudentRemoteProfileDto; companyId: string }) =>
      createStudentRemoteProfile(dto, companyId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['onboarding'] });
    },
  });
}

/** `PATCH /students-remote/:userId` — invalidates onboarding lists. */
export function useUpdateStudentRemoteProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      dto,
      companyId,
    }: {
      userId: string;
      dto: StudentRemoteProfileDto;
      companyId: string;
    }) => updateStudentRemoteProfile(userId, dto, companyId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['onboarding'] });
      void queryClient.invalidateQueries({ queryKey: sessionKeys.all });
    },
  });
}

/** React Query keys for remote onboarding lists and full-state. */
export const onboardingKeys = {
  all: ['onboarding'] as const,
  tutorsRemote: (filters: TutorFilters) =>
    [...onboardingKeys.all, 'tutor-remote', filters] as const,
  tutorRemoteDetail: (userId: string, companyId: string) =>
    [...onboardingKeys.all, 'tutor-remote', userId, companyId] as const,
  studentsRemote: (filters: StudentFilters) =>
    [...onboardingKeys.all, 'student-remote', filters] as const,
  studentRemoteDetail: (userId: string, companyId: string) =>
    [...onboardingKeys.all, 'student-remote', userId, companyId] as const,
  parentsRemote: (filters: ParentFilters) =>
    [...onboardingKeys.all, 'parent-remote', filters] as const,
  parentRemoteDetail: (userId: string, companyId: string) =>
    [...onboardingKeys.all, 'parent-remote', userId, companyId] as const,
  parentRemoteChildren: (parentUserId: string, companyId: string) =>
    [...onboardingKeys.all, 'parent-remote', parentUserId, 'children', companyId] as const,
  studentArchiveEligibility: (userId: string, companyId: string) =>
    [...onboardingKeys.all, 'student-remote', userId, companyId, 'archive-eligibility'] as const,
  studentReactivationEligibility: (userId: string, companyId: string) =>
    [
      ...onboardingKeys.all,
      'student-remote',
      userId,
      companyId,
      'reactivation-eligibility',
    ] as const,
  tutorArchiveEligibility: (userId: string, companyId: string) =>
    [...onboardingKeys.all, 'tutor-remote', userId, companyId, 'archive-eligibility'] as const,
  tutorReactivationEligibility: (userId: string, companyId: string) =>
    [...onboardingKeys.all, 'tutor-remote', userId, companyId, 'reactivation-eligibility'] as const,
  parentArchiveEligibility: (userId: string, companyId: string) =>
    [...onboardingKeys.all, 'parent-remote', userId, companyId, 'archive-eligibility'] as const,
  parentReactivationEligibility: (userId: string, companyId: string) =>
    [
      ...onboardingKeys.all,
      'parent-remote',
      userId,
      companyId,
      'reactivation-eligibility',
    ] as const,
};

/** `GET /tutors-remote` scoped to the signed-in company. */
export function useListTutorsRemoteScoped(
  filters: Omit<TutorFilters, 'companyId'>,
  options?: { enabled?: boolean },
): UseQueryResult<{ data: TutorRecord[]; total: number }, Error> {
  return useQuery({
    queryKey: onboardingKeys.tutorsRemote(filters as TutorFilters),
    queryFn: () => listTutorsRemoteScoped(filters),
    ...options,
  });
}

/** `GET /tutors-remote/:userId/full-state` */
export function useGetTutorRemoteFullState(
  userId: string,
  companyId: string,
  options?: { enabled?: boolean },
): UseQueryResult<TutorFullState, Error> {
  return useQuery({
    queryKey: onboardingKeys.tutorRemoteDetail(userId, companyId),
    queryFn: () => getTutorRemoteFullState(userId, companyId),
    ...options,
    retry: shouldRetryUnitGet,
  });
}

/** `POST /tutors-remote/:userId/information-session` — invalidates tutor full-state + onboarding. */
export function useMarkTutorInformationSessionParticipation(): UseMutationResult<
  TutorRecord,
  Error,
  { userId: string; companyId: string; sessionId?: string; participated?: boolean }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, companyId, sessionId, participated = true }) =>
      markInformationSessionParticipation(userId, companyId, sessionId, participated),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: onboardingKeys.tutorRemoteDetail(variables.userId, variables.companyId),
      });
      void queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
      toast.success(
        variables.participated === false
          ? 'Participation RI annulée'
          : 'Participation RI enregistrée',
      );
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

/** `GET /students-remote` scoped to the signed-in company. */
export function useListStudentsRemoteScoped(
  filters: Omit<StudentFilters, 'companyId'>,
  options?: { enabled?: boolean },
): UseQueryResult<{ data: StudentRecord[]; total: number }, Error> {
  return useQuery({
    queryKey: onboardingKeys.studentsRemote(filters as StudentFilters),
    queryFn: () => listStudentsRemoteScoped(filters),
    ...options,
  });
}

/** `GET /students-remote/:userId/full-state` */
export function useGetStudentRemoteFullState(
  userId: string,
  companyId: string,
  options?: { enabled?: boolean },
): UseQueryResult<StudentFullState, Error> {
  return useQuery({
    queryKey: onboardingKeys.studentRemoteDetail(userId, companyId),
    queryFn: () => getStudentRemoteFullState(userId, companyId),
    ...options,
    retry: shouldRetryUnitGet,
  });
}

/** `POST /students-remote/:userId/welcome-meeting` — invalidates student full-state, onboarding, sessions. */
export function useMarkStudentWelcomeMeetingParticipation(): UseMutationResult<
  StudentRecord,
  Error,
  { userId: string; companyId: string; sessionId?: string; participated?: boolean }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, companyId, sessionId, participated = true }) =>
      markWelcomeMeetingParticipation(userId, companyId, sessionId, participated),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: onboardingKeys.studentRemoteDetail(variables.userId, variables.companyId),
      });
      void queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
      void queryClient.invalidateQueries({ queryKey: sessionKeys.all });
      toast.success(
        variables.participated === false
          ? 'Participation RA annulée'
          : 'Participation RA enregistrée',
      );
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

/** `GET /parents-remote` scoped to the signed-in company. */
export function useListParentsRemoteScoped(
  filters: Omit<ParentFilters, 'companyId'>,
  options?: { enabled?: boolean },
): UseQueryResult<{ data: ParentRecord[]; total: number }, Error> {
  return useQuery({
    queryKey: onboardingKeys.parentsRemote(filters as ParentFilters),
    queryFn: () => listParentsRemoteScoped(filters),
    ...options,
  });
}

/** `GET /parents-remote` — paginated remote parent onboarding list. */
export function useListParentsRemote(
  filters: ParentFilters,
  options?: { enabled?: boolean },
): UseQueryResult<{ data: ParentRecord[]; total: number }, Error> {
  return useQuery({
    queryKey: onboardingKeys.parentsRemote(filters),
    queryFn: () => listParentsRemote(filters),
    ...options,
  });
}

/** `GET /parents-remote/:userId/full-state` */
export function useGetParentRemoteFullState(
  userId: string,
  companyId: string,
  options?: { enabled?: boolean },
): UseQueryResult<ParentFullState, Error> {
  return useQuery({
    queryKey: onboardingKeys.parentRemoteDetail(userId, companyId),
    queryFn: () => getParentRemoteFullState(userId, companyId),
    ...options,
    retry: shouldRetryUnitGet,
  });
}

/** `GET /parents-remote/:parentUserId/children` */
export function useGetParentRemoteChildren(
  parentUserId: string,
  companyId: string,
  options?: { enabled?: boolean },
): UseQueryResult<StudentRecord[], Error> {
  return useQuery({
    queryKey: onboardingKeys.parentRemoteChildren(parentUserId, companyId),
    queryFn: () => getParentRemoteChildren(parentUserId, companyId),
    ...options,
  });
}

/** `POST /users/prospects` — invite an onboarding prospect; invalidates onboarding lists. */
export function useCreateOnboardingProspect(): UseMutationResult<
  { user: unknown; invitationLink: string },
  Error,
  { prospectData: InviteProspectDto; companyId: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ prospectData, companyId }) => createProspect(prospectData, companyId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
    },
  });
}

/** React Query keys for tutor criminal-record files. */
export const criminalRecordKeys = {
  detail: (userId: string, companyId: string) => ['criminal-record', userId, companyId] as const,
};

/** `GET /tutors-presentiel/:userId/criminal-record` */
export function useGetTutorCriminalRecord(
  userId: string,
  companyId: string,
  options?: { enabled?: boolean },
): UseQueryResult<CriminalRecordDto | null, Error> {
  return useQuery({
    queryKey: criminalRecordKeys.detail(userId, companyId),
    queryFn: () => getCriminalRecord(userId, companyId),
    enabled: options?.enabled !== false && !!userId && !!companyId,
  });
}

/** `POST /tutors-presentiel/:userId/criminal-record/decision` — invalidates criminal-record, onboarding, tutors. */
export function useUpdateCriminalRecordDecision(): UseMutationResult<
  { criminalRecord: CriminalRecordDto; tutorPlatformStatus: string },
  Error,
  { userId: string; companyId: string; action: CriminalRecordDecisionAction }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, action }) => updateCriminalRecordDecision(userId, action),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: criminalRecordKeys.detail(variables.userId, variables.companyId),
      });
      void queryClient.invalidateQueries({
        queryKey: onboardingKeys.tutorRemoteDetail(variables.userId, variables.companyId),
      });
      void queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['tutors'] });
    },
  });
}

/** Invalidate onboarding, history, and optional session lists after a remote archive. */
function invalidateRemoteArchiveQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  userId: string,
  withSessions: boolean,
): void {
  void queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
  void queryClient.invalidateQueries({ queryKey: ['history', 'user', userId] });
  if (withSessions) {
    void queryClient.invalidateQueries({ queryKey: sessionKeys.all });
  }
}

/** `GET /students-remote/:userId/archive-eligibility` */
export function useGetStudentRemoteArchiveEligibility(
  userId: string,
  companyId: string,
  options?: { enabled?: boolean },
): UseQueryResult<ArchiveEligibility, Error> {
  return useQuery({
    queryKey: onboardingKeys.studentArchiveEligibility(userId, companyId),
    queryFn: () => getStudentRemoteArchiveEligibility(userId, companyId),
    enabled: options?.enabled !== false && !!userId && !!companyId,
  });
}

/** `POST /students-remote/:userId/archive` — invalidates onboarding, history, sessions. */
export function useArchiveStudentRemote(): UseMutationResult<
  ArchiveRemoteUserResult,
  Error,
  { userId: string; companyId: string; dto: ArchiveRemoteUserDto }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, companyId, dto }) => archiveStudentRemote(userId, companyId, dto),
    onSuccess: (_, variables) => {
      invalidateRemoteArchiveQueries(queryClient, variables.userId, true);
      toast.success('Archivage effectué');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

/** `GET /students-remote/:userId/reactivation-eligibility` */
export function useGetStudentRemoteReactivationEligibility(
  userId: string,
  companyId: string,
  options?: { enabled?: boolean },
): UseQueryResult<ReactivationEligibility, Error> {
  return useQuery({
    queryKey: onboardingKeys.studentReactivationEligibility(userId, companyId),
    queryFn: () => getStudentRemoteReactivationEligibility(userId, companyId),
    enabled: options?.enabled !== false && !!userId && !!companyId,
  });
}

/** `POST /students-remote/:userId/reactivate` — invalidates onboarding lists. */
export function useReactivateStudentRemote(): UseMutationResult<
  ReactivateRemoteUserResult,
  Error,
  { userId: string; companyId: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, companyId }) => reactivateStudentRemote(userId, companyId),
    onSuccess: (_, variables) => {
      invalidateRemoteArchiveQueries(queryClient, variables.userId, true);
      toast.success('Réactivation effectuée');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

/** `GET /tutors-remote/:userId/archive-eligibility` */
export function useGetTutorRemoteArchiveEligibility(
  userId: string,
  companyId: string,
  options?: { enabled?: boolean },
): UseQueryResult<ArchiveEligibility, Error> {
  return useQuery({
    queryKey: onboardingKeys.tutorArchiveEligibility(userId, companyId),
    queryFn: () => getTutorRemoteArchiveEligibility(userId, companyId),
    enabled: options?.enabled !== false && !!userId && !!companyId,
  });
}

/** `POST /tutors-remote/:userId/archive` — invalidates onboarding and history. */
export function useArchiveTutorRemote(): UseMutationResult<
  ArchiveRemoteUserResult,
  Error,
  { userId: string; companyId: string; dto: ArchiveRemoteUserDto }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, companyId, dto }) => archiveTutorRemote(userId, companyId, dto),
    onSuccess: (_, variables) => {
      invalidateRemoteArchiveQueries(queryClient, variables.userId, false);
      toast.success('Archivage effectué');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

/** `GET /tutors-remote/:userId/reactivation-eligibility` */
export function useGetTutorRemoteReactivationEligibility(
  userId: string,
  companyId: string,
  options?: { enabled?: boolean },
): UseQueryResult<ReactivationEligibility, Error> {
  return useQuery({
    queryKey: onboardingKeys.tutorReactivationEligibility(userId, companyId),
    queryFn: () => getTutorRemoteReactivationEligibility(userId, companyId),
    enabled: options?.enabled !== false && !!userId && !!companyId,
  });
}

/** `POST /tutors-remote/:userId/reactivate` — invalidates onboarding lists. */
export function useReactivateTutorRemote(): UseMutationResult<
  ReactivateRemoteUserResult,
  Error,
  { userId: string; companyId: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, companyId }) => reactivateTutorRemote(userId, companyId),
    onSuccess: (_, variables) => {
      invalidateRemoteArchiveQueries(queryClient, variables.userId, false);
      toast.success('Réactivation effectuée');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

/** `GET /parents-remote/:userId/archive-eligibility` */
export function useGetParentRemoteArchiveEligibility(
  userId: string,
  companyId: string,
  options?: { enabled?: boolean },
): UseQueryResult<ArchiveEligibility, Error> {
  return useQuery({
    queryKey: onboardingKeys.parentArchiveEligibility(userId, companyId),
    queryFn: () => getParentRemoteArchiveEligibility(userId, companyId),
    enabled: options?.enabled !== false && !!userId && !!companyId,
  });
}

/** `POST /parents-remote/:userId/archive` — invalidates onboarding and history. */
export function useArchiveParentRemote(): UseMutationResult<
  ArchiveRemoteUserResult,
  Error,
  { userId: string; companyId: string; dto: ArchiveRemoteUserDto }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, companyId, dto }) => archiveParentRemote(userId, companyId, dto),
    onSuccess: (_, variables) => {
      invalidateRemoteArchiveQueries(queryClient, variables.userId, false);
      toast.success('Archivage effectué');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

/** `GET /parents-remote/:userId/reactivation-eligibility` */
export function useGetParentRemoteReactivationEligibility(
  userId: string,
  companyId: string,
  options?: { enabled?: boolean },
): UseQueryResult<ReactivationEligibility, Error> {
  return useQuery({
    queryKey: onboardingKeys.parentReactivationEligibility(userId, companyId),
    queryFn: () => getParentRemoteReactivationEligibility(userId, companyId),
    enabled: options?.enabled !== false && !!userId && !!companyId,
  });
}

/** `POST /parents-remote/:userId/reactivate` — invalidates onboarding lists. */
export function useReactivateParentRemote(): UseMutationResult<
  ReactivateRemoteUserResult,
  Error,
  { userId: string; companyId: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, companyId }) => reactivateParentRemote(userId, companyId),
    onSuccess: (_, variables) => {
      invalidateRemoteArchiveQueries(queryClient, variables.userId, false);
      toast.success('Réactivation effectuée');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}
